import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import ModalBox from '../../ui-core/Modal';
import OpenEye from '../../static/images/signup/Openeye.svg';
import CloseEye from '../../static/images/signup/closeeye.svg';
import {
  deleteCommunityAccountRequest,
  extractApiError,
} from '../../api/community';
import { doLogoutRequest } from '../../store/action/auth';
import {
  createNotification,
  NOTIFICATION_TYPE,
} from '../../services/Notification';
import { requiresPasswordForSensitiveActions } from '../../utils/helpers';
import './style.css';

const DeleteAccount = ({ handleClose, show, userId }) => {
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const dispatch = useDispatch();
  const requiresPassword = requiresPasswordForSensitiveActions();

  useEffect(() => {
    if (!show) {
      setPassword('');
      setShowPassword(false);
      setPasswordError('');
      setLoading(false);
    }
  }, [show]);

  const getDeleteErrorMessage = error => {
    const fieldErrors = error?.response?.data?.errors;
    const passwordMessages = fieldErrors?.password;
    if (Array.isArray(passwordMessages) && passwordMessages[0]) {
      return passwordMessages[0];
    }
    if (typeof passwordMessages === 'string') {
      return passwordMessages;
    }
    return extractApiError(error, 'Failed to delete account.');
  };

  const handleDelete = async () => {
    if (requiresPassword && !password.trim()) {
      setPasswordError('Please enter your password.');
      return;
    }

    setLoading(true);
    setPasswordError('');
    try {
      await deleteCommunityAccountRequest(requiresPassword ? { password } : {});
      createNotification({
        type: NOTIFICATION_TYPE.SUCCESS,
        message: 'Your account has been deleted.',
      });
      handleClose();
      dispatch(doLogoutRequest());
    } catch (error) {
      const message = getDeleteErrorMessage(error);
      if (requiresPassword) setPasswordError(message);
      createNotification({
        type: NOTIFICATION_TYPE.ERROR,
        message,
      });
      setLoading(false);
    }
  };

  return (
    <ModalBox
      show={show}
      handleClose={handleClose}
      dialogClassName="delete-account-modal modal-dialog-centered"
      head="Delete Account"
    >
      <div className="delete-account-modal-body">
        <br />
        <p className="delete-account-modal-text">
          Are you sure you want to delete your account? This action cannot be
          undone, and all your data will be permanently removed.
        </p>
        {requiresPassword ? (
          <>
            <label
              className="account-settings-label"
              htmlFor="delete-account-password"
            >
              Password
            </label>
            <div className="change-password-input-row">
              <input
                id="delete-account-password"
                type={showPassword ? 'text' : 'password'}
                className="change-password-input"
                placeholder="Enter your password"
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                  if (passwordError) setPasswordError('');
                }}
                disabled={loading}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="change-password-toggle"
                onClick={() => setShowPassword(prev => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                disabled={loading}
              >
                <img src={showPassword ? OpenEye : CloseEye} alt="" />
              </button>
            </div>
            {passwordError ? (
              <span className="account-settings-error">{passwordError}</span>
            ) : null}
          </>
        ) : null}
        <div className="delete-account-modal-actions">
          <button
            type="button"
            className="delete-account-modal-btn delete-account-modal-btn--outline"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? (
              <Spinner animation="border" size="sm" role="status" />
            ) : (
              'Delete'
            )}
          </button>
          <button
            type="button"
            className="delete-account-modal-btn delete-account-modal-btn--filled"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </ModalBox>
  );
};

DeleteAccount.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default DeleteAccount;
